import { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { isPageLinkType } from '@/lib/blocks/button-links';
import type { ButtonConfig } from '@/types/button';
import { ButtonEditorForm } from './button-editor-form';

interface ButtonSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValue: ButtonConfig;
  onSave: (value: ButtonConfig) => void;
}

export function ButtonSettingsDialog({
  open,
  onOpenChange,
  initialValue,
  onSave,
}: ButtonSettingsDialogProps) {
  const [draft, setDraft] = useState<ButtonConfig>(initialValue);

  useEffect(() => {
    if (open) setDraft(initialValue);
  }, [open, initialValue]);

  const linkType = draft.linkType || 'external';
  const hasValidDestination = isPageLinkType(linkType)
    ? Boolean(draft.internalPageId)
    : Boolean(draft.link?.trim());

  const handleSave = useCallback(() => {
    onSave(draft);
    onOpenChange(false);
  }, [draft, onSave, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Button</DialogTitle>
          <DialogDescription>Configure the button label, style, and link.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <ButtonEditorForm
            initialValue={initialValue}
            resetKey={open}
            enableSearch={open}
            onChange={setDraft}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" colorScheme="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!hasValidDestination}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
